import type { QcsColor } from 'app-components/flux/transactions/transactionsTypes';

import type {
  CleanupTransactionEligibleTransaction,
  CleanupTransactionProposal,
  CleanupTransactionSuggestion,
} from './types';

const USER_FLAG_VALUES = new Set<QcsColor>([
  'RED',
  'ORANGE',
  'YELLOW',
  'GREEN',
  'BLUE',
  'VIOLET',
  'BLACK',
  'BROWN',
  'NONE',
]);

const arraysHaveSameValues = (a: string[], b: string[]): boolean =>
  a.length === b.length && new Set(a).size === new Set(b).size && a.every((value) => b.includes(value));

const validateSuggestion = (
  rawSuggestion: unknown,
  eligibleTransaction: CleanupTransactionEligibleTransaction,
  allowedTagNames: Set<string>,
): CleanupTransactionSuggestion | null => {
  if (!rawSuggestion || typeof rawSuggestion !== 'object') {
    return null;
  }

  const candidate = rawSuggestion as Record<string, unknown>;
  const { field, proposedValue, proposedCategoryId, proposedTags, rationale } = candidate;

  if (typeof field !== 'string' || typeof rationale !== 'string') {
    return null;
  }

  const confidence = typeof candidate.confidence === 'number' ? Math.min(1, Math.max(0, candidate.confidence)) : 0;

  switch (field) {
    case 'payee': {
      const trimmed = typeof proposedValue === 'string' ? proposedValue.trim() : '';
      if (!trimmed || trimmed === eligibleTransaction.payee) return null;
      return { field, currentValue: eligibleTransaction.payee, proposedValue: trimmed, confidence, rationale };
    }
    case 'memo': {
      const trimmed = typeof proposedValue === 'string' ? proposedValue.trim() : '';
      if (!trimmed || trimmed === eligibleTransaction.memo) return null;
      return { field, currentValue: eligibleTransaction.memo, proposedValue: trimmed, confidence, rationale };
    }
    case 'category': {
      if (typeof proposedCategoryId !== 'string' || proposedCategoryId === eligibleTransaction.categoryId) {
        return null;
      }
      // The display name is always resolved from the allowed-categories list by id, never taken
      // from the model directly - this guarantees what's shown to the user can never diverge
      // from the category actually applied (the id is the only thing setCoa acts on).
      const allowedCategory = eligibleTransaction.allowedCategories.find(
        (category) => category.id === proposedCategoryId,
      );
      if (!allowedCategory) return null;
      return {
        field,
        currentValue: eligibleTransaction.currentCategoryName,
        proposedValue: allowedCategory.name,
        proposedCategoryId,
        confidence,
        rationale,
      };
    }
    case 'tags': {
      if (!Array.isArray(proposedTags)) return null;
      const uniqueProposedTags = Array.from(new Set(proposedTags));
      const validatedTags = uniqueProposedTags.filter(
        (tag): tag is string => typeof tag === 'string' && allowedTagNames.has(tag),
      );
      if (validatedTags.length !== uniqueProposedTags.length) return null;
      if (arraysHaveSameValues(validatedTags, eligibleTransaction.tags)) return null;
      return {
        field,
        currentValue: eligibleTransaction.tags,
        proposedValue: validatedTags,
        confidence,
        rationale,
      };
    }
    case 'user_flag': {
      if (typeof proposedValue !== 'string' || !USER_FLAG_VALUES.has(proposedValue as QcsColor)) return null;
      if (proposedValue === eligibleTransaction.userFlag) return null;
      return { field, currentValue: eligibleTransaction.userFlag, proposedValue, confidence, rationale };
    }
    default:
      return null;
  }
};

export const validateCleanupTransactionSuggestions = (
  eligibleTransaction: CleanupTransactionEligibleTransaction,
  allowedTagNames: Set<string>,
  parsedResponse: unknown,
): CleanupTransactionProposal => {
  const rawSuggestions =
    parsedResponse && typeof parsedResponse === 'object'
      ? (parsedResponse as Record<string, unknown>).suggestions
      : null;

  const seenFields = new Set<CleanupTransactionSuggestion['field']>();
  const suggestions = Array.isArray(rawSuggestions)
    ? rawSuggestions.reduce<CleanupTransactionSuggestion[]>((acc, rawSuggestion) => {
        const suggestion = validateSuggestion(rawSuggestion, eligibleTransaction, allowedTagNames);
        // The schema doesn't stop the model from returning more than one suggestion for the same
        // field - only the first is kept, since downstream (drawer apply + summary list) assumes
        // at most one suggestion per field.
        if (suggestion && !seenFields.has(suggestion.field)) {
          seenFields.add(suggestion.field);
          acc.push(suggestion);
        }
        return acc;
      }, [])
    : [];

  return { transactionId: eligibleTransaction.transactionId, suggestions };
};

// Framework-shaped: takes the eligibleTransactions array and returns a proposals array,
// same contract as every other structured-model capability's parseStructuredResponse.
// Always [] or [proposal] in practice, since cleanup_transaction only ever has one
// eligible transaction (see runner.ts's length !== 1 guard).
export const parseCleanupTransactionAssistantResponse = (
  eligibleTransactions: CleanupTransactionEligibleTransaction[],
  assistantResponse: string,
): CleanupTransactionProposal[] | null => {
  const eligibleTransaction = eligibleTransactions[0];
  if (!eligibleTransaction) {
    return null;
  }

  const trimmedAssistantResponse = assistantResponse.trim();
  if (!trimmedAssistantResponse) {
    return null;
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmedAssistantResponse);
  } catch {
    return null;
  }

  if (!parsed || typeof parsed !== 'object' || !Array.isArray((parsed as Record<string, unknown>).suggestions)) {
    return null;
  }

  const allowedTagNames = new Set(eligibleTransaction.allowedTags);

  return [validateCleanupTransactionSuggestions(eligibleTransaction, allowedTagNames, parsed)];
};
